import Vue from 'vue'
import Vuex, {StoreOptions} from 'vuex'
import Store from '@/modules/core/vuex/Store'
import {InputStyle, NavigationMode, RootState, Version} from '@/store/types'
import mutations from '@/store/mutations'
import actions from '@/store/actions'
import authStore from '@/modules/auth/store'
import authorizationStore from '@/modules/authorization/store'
import {Config} from '@/modules/core/config'
import packages from '../../package.json'
import { default as logStore } from '@/modules/log/store'

/**
 * Modulo principal de Vuex
 *
 * Inicia el estado raíz de la aplicación y registra módulos, mutaciones y acciones
 */
Vue.use(Vuex)

const options: StoreOptions<RootState> = {
    state: {
        version: Version.fromString(packages.version),
        notification: {
            visible: false,
            message: '',
            color: '',
            center: {
                visible: false,
                stack: [],
                unread: 0,
            },
        },
        loadingScreen: {
            visible: false,
            message: 'Cargando',
            fullscreen: false,
            brand: false,
            determinate: false,
            progress: 0,
            progressMessage: '',
        },
        toolbar: {
            visible: true,
            title: '',
        },
        sidenav: {
            visible: true,
        },
        enabledModules: [],
        enabledModulesFetched: false,
        menuItems: [],
        dashButtons: [],
        stopNavigation: true,
        config: new Config(),
        themes: [],
        currentTheme: { name: '', key: '', vuetifyTheme: {}},
        themeSelector: false,
        inputStyle: InputStyle.Classic,
        darkMode: false,
        permanentNavigation: true,
        displayTemporalNavigation: false,
        navigationMode: NavigationMode.OpenOnHover,
        displaySizeOverlay: false,
    },
    mutations,
    getters: {
        /**
         * Comprueba si el módulo argumentado se encuentra activo en la aplicación
         *
         * @param state
         */
        moduleIsEnabled: state => (module: string) => !!state.enabledModules.find(m => m === module),
    },
    actions,
    modules: {
        // Autenticación y autorización siempre se encuentra disponibles
        auth: authStore,
        authorization: authorizationStore,
        log: logStore,
    },
}


const store = new Store<RootState>(options)

export type ValidModuleName = 'geo' | 'media' | 'seo' | 'cms' | 'payment' | 'log' | 'product' | 'reservation' | 'user' | 'calendar' | 'widget' | 'authorization' | 'blog' | string

const moduleMap: {[key: string]: () => Promise<any>} = {
    i18n: () => import(/* webpackChunkName: "i18n.store" */ '@/modules/i18n/store'),
    auth: () => import(/* webpackChunkName: "auth.store" */ '@/modules/auth/store'),
    blog: () => import(/* webpackChunkName: "blog.store" */ '@/modules/blog/store'),
    geo: () => import(/* webpackChunkName: "geo.store" */ '@/modules/geo/store'),
    media: () => import(/* webpackChunkName: "media.store" */ '@/modules/media/store'),
    seo: () => import(/* webpackChunkName: "seo.store" */ '@/modules/seo/store'),
    cms: () => import(/* webpackChunkName: "cms.store" */ '@/modules/cms/store'),
    payment: () => import(/* webpackChunkName: "payment.store" */ '@/modules/payment/store'),
    log: () => import(/* webpackChunkName: "log.store" */ '@/modules/log/store'),
    product: () => import(/* webpackChunkName: "product.store" */'@/modules/product/store'),
    reservation: () => import(/* webpackChunkName: "reservation.store" */'@/modules/reservation/store'),
    user: () => import(/* webpackChunkName: "user.store" */'@/modules/user/store'),
    calendar: () => import(/* webpackChunkName: "calendar.store" */'@/modules/calendar/store'),
    widget: () => import(/* webpackChunkName: "widget.store" */'@/modules/widget/store'),
    authorization: () => import(/* webpackChunkName: "authorization.store" */'@/modules/authorization/store'),
    shipping: () => import(/* webpackChunkName: "shipping.store" */'@/modules/shipping/store'),
    appRouter: () => import(/* webpackChunkName: "appRouter.store" */ '@/modules/app-router/store'),
    support: () => import(/* webpackChunkName: "support.store" */ '@/modules/support/store'),
}

const loaded: {[key: string]: boolean} = {}

export async function loadModule (...modules: ValidModuleName[]) {
    for (const name of modules) {
        // El módulo de vuex ya se encuentra registrado
        if (loaded[name]) {
            continue
        }

        const importer =  await moduleMap[name]
        if (!importer) {
            // tslint:disable-next-line:no-console
            console.error(`No existe ningún módulo con el nombre ${name}`)
            continue
        }

        const module = await importer()
        store.registerModule(name, module.default)
        loaded[name] = true
    }
}

export default store
